import React, { useCallback, useEffect, useRef, useState } from 'react';

import "./stylesAllSports.css";
import "../../CasinoFM/CasinoRelated/SortingTable/stylesSortingTable.css";
import "../SportsRelated/CategoryRow/stylesCategoryRow.css";

import ClipPath from '../../../../assets/icons/ic_clip-path.svg';
import Planet from '../../../../assets/icons/ic_planet.svg';

import { DownOutlined, PushpinFilled, PushpinOutlined, UpOutlined, TrophyOutlined, LoadingOutlined } from "@ant-design/icons";
import Sortable from "sortablejs";
import { Select } from "antd";
import { message } from "antd";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { SPORT_LOGO_BASE_URL } from "../../../../utils/Constants";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import ToggleSwitch from "../../../CommonComponents/ToggleSwitch/ToggleSwitch";
import { useQuery, useMutation } from "@tanstack/react-query";

function AllSports() {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );

  const allSportsTableBodyRef = useRef(null);
  const sportsSortableRef = useRef(null);
  const countryRefs = useRef({});
  const pinnedLeaguesRefs = useRef({});
  const leagueRefs = useRef({});

  const [allSports, setAllSports] = useState([]);
  const [, setApiIsRunning] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [openedSports, setOpenedSports] = useState([]);
  const [openedCountries, setOpenedCountries] = useState([]);
  const [pinnedLeagues, setPinnedLeagues] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const triggerAllSportsCacheUpdate = useCallback(async () => {
    // Fire-and-forget because the backend endpoint still returns incorrect statuses ahead of code freeze.
    try {
      await PrivateAdminPanelInstance.post(API_ROUTES.UPDATE_ALL_SPORTS_S3);
    } catch (error) {
      console.error("[AllSports] Failed to update All Sports cache", error);
    }
  }, [PrivateAdminPanelInstance]);

  const handleSaveChanges = useCallback(async () => {
    if (!hasUnsavedChanges) return;
    
    setIsSaving(true);
    try {
      await triggerAllSportsCacheUpdate();
      setHasUnsavedChanges(false);
      message.success('Changes saved successfully!');
    } catch (error) {
      console.error("[AllSports] Failed to save changes", error);
      message.error('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [hasUnsavedChanges, triggerAllSportsCacheUpdate]);

  const reorderLeaguesWithPinnedFirst = useCallback((leagues, prioritizedPinnedId = null) => {
    if (!Array.isArray(leagues)) return [];

    const pinned = [];
    const others = [];

    leagues.forEach((league) => {
      if (league?.is_pinned) {
        pinned.push(league);
      } else {
        others.push(league);
      }
    });

    const sortedPinned = pinned
      .slice()
      .sort((a, b) => {
        if (prioritizedPinnedId) {
          if (a.id === prioritizedPinnedId) return -1;
          if (b.id === prioritizedPinnedId) return 1;
        }
        return (a.order ?? 0) - (b.order ?? 0);
      });

    const sortedOthers = others
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return [...sortedPinned, ...sortedOthers].map((league, index) => ({
      ...league,
      order: index,
    }));
  }, []);

  // Helper: resolve sport logo URL by slugifying sport name and applying overrides
  const getSportLogoUrl = (sportName) => {
    if (!sportName) return null;
    const overrides = {
      // normalized name (lowercase, no spaces/punctuation) : filename
      soccer: "soccer.png",
      football: "americanfootball.png",
      americanfootball: "americanfootball.png",
      basketball: "basketball.png",
      baseball: "baseball.png",
      tennis: "tennis.png",
      tabletennis: "tabletennis.png",
      icehockey: "icehockey.png",
      hockey: "icehockey.png",
      volleyball: "volleyball.png",
      handball: "handball.png",
      darts: "darts.png",
      mma: "mma.png",
      boxing: "boxing.png",
      cricket: "cricket.png",
      rugby: "rugby.png",
    };
    const normalized = String(sportName)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    const file = overrides[normalized] || `${normalized}.png`;
    return `${SPORT_LOGO_BASE_URL}/${file}`;
  };

  const updateSportStatus = (payload) => {
    console.log("[AllSports] TOGGLE SPORT STATUS:", payload);
    const sportId = payload.event_code;
    const nextIsActive = payload.is_active;

    const previousState = [...allSports.map((s) => ({ ...s }))];

    const updated = allSports.map((s) =>
      s.id === sportId ? { ...s, status: nextIsActive } : s
    );

    setAllSports(updated);

    const toastKey = `sport-status-${sportId}`;
    const successMessage = nextIsActive
      ? "Sport activated successfully"
      : "Sport deactivated successfully";
    message.open({ key: toastKey, type: "success", content: successMessage });

    (async () => {
      try {
        const res = await PrivateAdminPanelInstance.post(
          API_ROUTES.SET_ENTITY_ACTIVE_STATUS,
          {
            entity_type: "ALL_SPORTS",
            entity_id: `SPORT#${sportId}`,
            is_active: nextIsActive,
          }
        );
        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }
        setHasUnsavedChanges(true);
      } catch (err) {
        console.error("Error updating sport status", err);
        message.open({
          key: toastKey,
          type: "error",
          content: "Error updating sport status. Reverting change.",
        });
        setAllSports(previousState);
      }
    })();
  };

  const updateCountryStatus = (payload) => {
    console.log("[AllSports] TOGGLE COUNTRY STATUS:", payload);
    const compositeId = payload.event_code; // "<sport_id>#<country_id>"
    const nextIsActive = payload.is_active;
    const [sportId] = compositeId.split("#");

    const previousState = [...allSports.map((s) => ({
      ...s,
      countries: s.countries?.map((c) => ({ ...c, leagues: c.leagues?.map((l) => ({ ...l })) })) || [],
    }))];

    const updated = allSports.map((s) => {
      if (s.id !== sportId) return s;
      return {
        ...s,
        countries: (s.countries || []).map((c) =>
          c.id === compositeId
            ? {
                ...c,
                status: nextIsActive,
                leagues: (c.leagues || []).map((l) => ({ ...l, status: nextIsActive })),
              }
            : c
        ),
      };
    });

    setAllSports(updated);

    const toastKey = `country-status-${compositeId}`;
    const successMessage = nextIsActive
      ? "Country activated successfully"
      : "Country deactivated successfully";
    message.open({ key: toastKey, type: "success", content: successMessage });

    (async () => {
      try {
        const res = await PrivateAdminPanelInstance.post(
          API_ROUTES.SET_ENTITY_ACTIVE_STATUS,
          {
            entity_type: "ALL_SPORTS",
            entity_id: `COUNTRY#${compositeId}`,
            is_active: nextIsActive,
          }
        );
        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }
        setHasUnsavedChanges(true);
      } catch (err) {
        console.error("Error updating country status", err);
        message.open({
          key: toastKey,
          type: "error",
          content: "Error updating country status. Reverting change.",
        });
        setAllSports(previousState);
      }
    })();
  };

  const updateLeagueStatus = (payload) => {
    console.log("[AllSports] TOGGLE LEAGUE STATUS:", payload);
    const leagueId = payload.event_code; // already like "LEAGUE#..."
    const nextIsActive = payload.is_active;

    const previousState = [...allSports.map((s) => ({
      ...s,
      countries: s.countries?.map((c) => ({ ...c, leagues: c.leagues?.map((l) => ({ ...l })) })) || [],
    }))];

    const updated = allSports.map((s) => ({
      ...s,
      countries: (s.countries || []).map((c) => ({
        ...c,
        leagues: (c.leagues || []).map((l) => (l.id === leagueId ? { ...l, status: nextIsActive } : l)),
      })),
    }));

    setAllSports(updated);

    const toastKey = `league-status-${leagueId}`;
    const successMessage = nextIsActive
      ? "League activated successfully"
      : "League deactivated successfully";
    message.open({ key: toastKey, type: "success", content: successMessage });

    (async () => {
      try {
        const res = await PrivateAdminPanelInstance.post(
          API_ROUTES.SET_ENTITY_ACTIVE_STATUS,
          {
            entity_type: "ALL_SPORTS",
            entity_id: leagueId,
            is_active: nextIsActive,
          }
        );
        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }
        setHasUnsavedChanges(true);
      } catch (err) {
        console.error("Error updating league status", err);
        message.open({
          key: toastKey,
          type: "error",
          content: "Error updating league status. Reverting change.",
        });
        setAllSports(previousState);
      }
    })();
  };

  const callPinLeague = async (entityType, league) => {
    console.log("[AllSports] PIN LEAGUE:", { entityType, entity_id: league.id });
    const res = await PrivateAdminPanelInstance.post(API_ROUTES.PIN_ENTITY, {
      entity_type: entityType,
      entity_id: league.id,
    });
    if (res.status !== 200) throw new Error(`Status ${res.status}`);
    setHasUnsavedChanges(true);
  };

  const callUnPinLeague = async (entityType, league) => {
    console.log("[AllSports] UNPIN LEAGUE:", { entityType, entity_id: league.id });
    const res = await PrivateAdminPanelInstance.post(API_ROUTES.UNPIN_ENTITY, {
      entity_type: entityType,
      entity_id: league.id,
    });
    if (res.status !== 200) throw new Error(`Status ${res.status}`);
    setHasUnsavedChanges(true);
  };
  // Countries are not pinnable in All Sports per product decision

  const pinLeague = async (sportId, league) => {
    const toastKey = `league-pin-${league.id}`;
    const previousPinned = pinnedLeagues.map((l) => ({ ...l }));
    const previousSports = allSports.map((sport) => ({
      ...sport,
      countries: (sport.countries || []).map((country) => ({
        ...country,
        leagues: (country.leagues || []).map((l) => ({ ...l })),
      })),
    }));

    const nextPinned = [
      {
        ...league,
        order: 0,
        sport_id: sportId,
        reorder_id: league.reorder_id || league.id?.replace("LEAGUE#", ""),
      },
      ...previousPinned.map((l, index) => ({
        ...l,
        order: index + 1,
      })),
    ];

    const nextSports = allSports.map((sport) => {
      if (sport.id !== sportId) return sport;
      return {
        ...sport,
        countries: (sport.countries || []).map((country) => {
          const hasLeague = (country.leagues || []).some((l) => l.id === league.id);
          if (!hasLeague) return country;

          const updatedLeagues = (country.leagues || []).map((l) =>
            l.id === league.id ? { ...l, is_pinned: true } : l
          );

          return {
            ...country,
            leagues: reorderLeaguesWithPinnedFirst(updatedLeagues, league.id),
          };
        }),
      };
    });

    try {
      setApiIsRunning(true);
      setPinnedLeagues(nextPinned);
      setAllSports(nextSports);
      message.open({ key: toastKey, type: "success", content: "League pinned successfully!" });
      await pinLeagueMutation.mutateAsync({ league });
    } catch (err) {
      setPinnedLeagues(previousPinned.map((l) => ({ ...l })));
      setAllSports(previousSports);
      if (err?.response?.status === 404) {
        message.open({
          key: toastKey,
          type: "error",
          content:
            "League not available under All Sports yet (backend seeding pending).",
        });
      } else {
        message.open({
          key: toastKey,
          type: "error",
          content: "Error pinning league",
        });
      }
      console.error("Error pinning league", err);
    } finally {
      setApiIsRunning(false);
    }
  };

  const unPinLeague = async (_sportId, league) => {
    const toastKey = `league-pin-${league.id}`;
    const previousPinned = pinnedLeagues.map((l) => ({ ...l }));
    const previousSports = allSports.map((sport) => ({
      ...sport,
      countries: (sport.countries || []).map((country) => ({
        ...country,
        leagues: (country.leagues || []).map((l) => ({ ...l })),
      })),
    }));

    const nextPinned = pinnedLeagues
      .filter((l) => l.id !== league.id)
      .map((l, index) => ({
        ...l,
        order: index,
      }));

    const nextSports = allSports.map((sport) => {
      if (sport.id !== _sportId) return sport;
      return {
        ...sport,
        countries: (sport.countries || []).map((country) => {
          const hasLeague = (country.leagues || []).some((l) => l.id === league.id);
          if (!hasLeague) return country;

          const updatedLeagues = (country.leagues || []).map((l) =>
            l.id === league.id ? { ...l, is_pinned: false } : l
          );

          return {
            ...country,
            leagues: reorderLeaguesWithPinnedFirst(updatedLeagues),
          };
        }),
      };
    });

    try {
      setApiIsRunning(true);
      setPinnedLeagues(nextPinned);
      setAllSports(nextSports);
      message.open({ key: toastKey, type: "success", content: "League unpinned successfully!" });
      await unpinLeagueMutation.mutateAsync({ league });
    } catch (err) {
      message.open({
        key: toastKey,
        type: "error",
        content: "Error unpinning league",
      });
      setPinnedLeagues(previousPinned.map((l) => ({ ...l })));
      setAllSports(previousSports);
      console.error('Error unpinning league', err);
    } finally {
      setApiIsRunning(false);
    }
  };
  // No country pin/unpin actions

  // Stable API call for reordering entities (SPORT/COUNTRY/LEAGUE)
  const reorderEntities = useCallback(async (
    itemType = "SPORT",
    orderedIds,
    parentId = null
  ) => {
    let params = {
      entity_type: "ALL_SPORTS",
      item_type: "SPORT",
      ordered_ids: orderedIds,
    };
    if (itemType === "COUNTRY" || itemType === "LEAGUE") {
      params.parent_id = parentId;
      params.item_type = itemType;
    }
    try {
      console.log("[AllSports] reorderEntities params:", params);
      setApiIsRunning(true);
      const res = await PrivateAdminPanelInstance.post(
        API_ROUTES.REORDER_ENTITIES,
        params
      );
      if (res.status !== 200) throw new Error(`Status ${res.status}`);
      setHasUnsavedChanges(true);
      return res.data;
    } catch (error) {
      console.log("error reordering entities", error);
      throw error;
    } finally {
      setApiIsRunning(false);
    }
  }, [PrivateAdminPanelInstance, triggerAllSportsCacheUpdate]);

  const updateLeagueOrder = useCallback(async (
    sportId,
    countryId,
    oldIndex,
    newIndex
  ) => {
    setAllSports((prevSports) =>
      prevSports.map((sport) => {
        if (sport.id !== sportId) return sport;
        return {
          ...sport,
          countries: sport.countries.map((country) => {
            if (country.id !== countryId) return country;
            const newLeagues = [...country.leagues];
            const [movedItem] = newLeagues.splice(oldIndex, 1);
            newLeagues.splice(newIndex, 0, movedItem);
            const reorderedLeagues = newLeagues.map((item, index) => ({
              ...item,
              order: index,
            }));

            const previousState = prevSports;
            const toastKey = `league-order-${countryId}`;
            message.open({
              key: toastKey,
              type: "success",
              content: "Order updated successfully!",
            });
            (async () => {
            try {
              const parentForReorder =
                reorderedLeagues.find((league) => league.parent_country_id)?.parent_country_id ||
                country.league_parent_reference ||
                country.entity_id;
              await reorderEntities(
                "LEAGUE",
                reorderedLeagues.map((league) => league.reorder_id),
                parentForReorder
              );
            } catch (err) {
                console.error("Error updating backend", err);
                setAllSports(previousState);
                message.open({
                  key: toastKey,
                  type: "error",
                  content: "Error updating order. Please try again.",
                });
              }
            })();

            return { ...country, leagues: reorderedLeagues };
          }),
        };
      })
    );
  }, [setAllSports, reorderEntities]);

  const updateCountryOrder = useCallback((sportId, oldIndex, newIndex) => {
    setAllSports((prevSports) =>
      prevSports.map((sport) => {
        if (sport.id !== sportId) return sport;

        const updatedCountries = [...sport.countries];
        const [movedCountry] = updatedCountries.splice(oldIndex, 1);
        updatedCountries.splice(newIndex, 0, movedCountry);

        const reordered = updatedCountries.map((c, i) => ({
          ...c,
          order: i,
        }));

        const previousState = prevSports;
        const toastKey = `country-order-${sportId}`;
        message.open({
          key: toastKey,
          type: "success",
          content: "Order updated successfully!",
        });

        (async () => {
          try {
            await reorderEntities(
              "COUNTRY",
              reordered.map((country) => country.id),
              sport.id
            );
          } catch (err) {
            console.error("Error updating backend", err);
            setAllSports(previousState);
            message.open({
              key: toastKey,
              type: "error",
              content: "Error updating order. Please try again.",
            });
          }
        })();

        return {
          ...sport,
          countries: reordered,
        };
      })
    );
  }, [setAllSports, reorderEntities]);

  

  const pinLeagueMutation = useMutation({
    mutationFn: ({ league }) => callPinLeague("ALL_SPORTS", league),
  });

  const unpinLeagueMutation = useMutation({
    mutationFn: ({ league }) => callUnPinLeague("ALL_SPORTS", league),
  });

  // Countries are not pinnable in All Sports

  const getAllSports = async () => {
    try {
      setApiIsRunning(true);
      const requests = [
        PrivateAdminPanelInstance.post(API_ROUTES.GET_SPORTS_ORDERING, {
          entity_type: "ALL_SPORTS",
        }),
        PrivateAdminPanelInstance.post(API_ROUTES.GET_ENTITIES_BY_TYPE, {
          entity_type: "ALL_SPORTS",
          item_type: "COUNTRY",
          include_inactive: true,
        }),
        PrivateAdminPanelInstance.post(API_ROUTES.GET_ENTITIES_BY_TYPE, {
          entity_type: "ALL_SPORTS",
          item_type: "LEAGUE",
          include_inactive: true,
        }),
      ];
      const results = await Promise.allSettled(requests);
      const sportsRes = results[0];
      const countriesRes = results[1];
      const leaguesRes = results[2];
      const sports =
        sportsRes.status === "fulfilled"
          ? sportsRes.value.data?.data?.sports || []
          : [];
      const countries =
        countriesRes.status === "fulfilled"
          ? countriesRes.value.data?.data?.entities || []
          : [];
      const leagues =
        leaguesRes.status === "fulfilled"
          ? leaguesRes.value.data?.data?.entities || []
          : [];

      console.log("[AllSports] sports:", sports);
      console.log("[AllSports] countries:", countries);
      console.log("[AllSports] leagues:", leagues);

      const formatted = sports.map((s) => ({
        id: s.sport_id,
        name: s.name,
        order: s.display_order,
        status: s.is_active,
        is_pinned: s.is_pinned,
        countries: countries
          .filter((c) => c.parent_sport_id === s.sport_id)
          .sort((a, b) => {
            const ao = a.display_order ?? 0;
            const bo = b.display_order ?? 0;
            return ao - bo;
          })
          .map((country, index) => {
            const countryIdComposite = `${country.parent_sport_id}#${country.country_id}`;
            const childLeagues = leagues
              .filter((l) => {
                const parentId = l.parent_country_id;
                const composite = `COUNTRY#${country.parent_sport_id}#${country.country_id}`;
                // Accept both composite and raw country_id forms for safety
                return (
                  parentId === composite ||
                  parentId === country.entity_id ||
                  parentId === country.country_id
                );
              })
              .sort((a, b) => {
                const ap = a.is_pinned ? 0 : 1;
                const bp = b.is_pinned ? 0 : 1;
                if (ap !== bp) return ap - bp;
                const ao = a.pinned_order ?? a.display_order ?? 0;
                const bo = b.pinned_order ?? b.display_order ?? 0;
                return ao - bo;
              })
              .map((l, idx) => ({
                id: l.entity_id,
                reorder_id: l.entity_id.replace("LEAGUE#", ""),
                name: l.name,
                order: idx,
                status: l.is_active,
                is_pinned: !!l.is_pinned,
                logo_url: l.league_logo_url || null,
                parent_country_id: l.parent_country_id || null,
              }));

            return {
              id: countryIdComposite,
              entity_id: `COUNTRY#${countryIdComposite}`,
              country_id: country.country_id,
              league_parent_reference:
                (childLeagues[0] && childLeagues[0].parent_country_id) ||
                country.entity_id ||
                country.country_id,
              name: country.country_name,
              order: index,
              status: country.is_active,
              flag_url: country.country_flag_url || null,
              leagues: childLeagues,
            };
          }),
      }));

      console.log("[AllSports] formatted:", formatted);

      const pinned = leagues
        .filter((l) => l.is_pinned)
        .reduce((acc, l) => {
          const key = `${l.sport_id}__${l.league_id}`;
          if (!acc.map.has(key)) {
            acc.map.set(key, true);
            acc.items.push({
              id: l.entity_id,
              reorder_id: l.entity_id.replace("LEAGUE#", ""),
              name: l.name,
              order: l.pinned_order ?? 0,
              status: l.is_active,
              sport_id: l.sport_id,
              logo_url: l.league_logo_url || null,
            });
          }
          return acc;
        }, { map: new Map(), items: [] }).items
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      setAllSports(formatted);
      setPinnedLeagues(pinned);
    } catch (e) {
      console.log("error getting all sports", e);
    } finally {
      setApiIsRunning(false);
      setDataLoaded(true);
    }
  };

  // React Query: fetch All Sports data
  useQuery({
    queryKey: ["all-sports", "sports-countries-leagues"],
    queryFn: getAllSports,
    refetchOnWindowFocus: false,
    onSuccess: () => {},
  });

  useEffect(() => {
    if (!allSportsTableBodyRef.current) return;
    // Clean existing instance (StrictMode double-invoke safe)
    if (sportsSortableRef.current) {
      sportsSortableRef.current.destroy();
      sportsSortableRef.current = null;
    }
    sportsSortableRef.current = Sortable.create(allSportsTableBodyRef.current, {
      animation: 150,
      onEnd: (evt) => {
        setAllSports((prevSports) => {
          if (!prevSports.length) return prevSports;
          const newOrder = [...prevSports];
          const [movedItem] = newOrder.splice(evt.oldIndex, 1);
          newOrder.splice(evt.newIndex, 0, movedItem);
          const updatedOrder = newOrder.map((item, idx) => ({
            ...item,
            order: idx,
          }));
          const previousState = prevSports;
          const toastKey = `sport-order-${movedItem?.id || "unknown"}`;
          message.open({
            key: toastKey,
            type: "success",
            content: "Order updated successfully!",
          });
          (async () => {
            try {
              await reorderEntities(
                "SPORT",
                updatedOrder.map((sport) => sport.id),
                null
              );
            } catch (err) {
              console.error("Error updating backend", err);
              setAllSports(previousState);
              message.open({
                key: toastKey,
                type: "error",
                content: "Error updating order. Please try again.",
              });
            }
          })();
          return updatedOrder;
        });
      },
    });
    return () => {
      if (sportsSortableRef.current) {
        sportsSortableRef.current.destroy();
        sportsSortableRef.current = null;
      }
    };
  }, [reorderEntities]);

  useEffect(() => {
    if (!countryRefs.current) return;
    const sortables = [];
    Object.entries(countryRefs.current).forEach(([_countryId, el]) => {
      if (!el) return;
      const sortable = Sortable.create(el, {
        animation: 150,
        handle: '.drag-handle-country',
        onEnd: (evt) => {
          const sportId = evt.item.dataset.sportId;
          updateCountryOrder(sportId, evt.oldIndex, evt.newIndex);
        },
      });
      sortables.push(sortable);
    });
    return () => {
      sortables.forEach((s) => s.destroy());
    };
  }, [openedSports, updateCountryOrder]);

  useEffect(() => {
    if (!leagueRefs.current) return;
    const sortables = [];
    Object.entries(leagueRefs.current).forEach(([_countryId, el]) => {
      if (!el) return;
      const sortable = Sortable.create(el, {
        animation: 150,
        handle: '.drag-handle-league',
        onEnd: (evt) => {
          const sportId = evt.item.dataset.sportId;
          const countryId = evt.item.dataset.countryId;
          updateLeagueOrder(sportId, countryId, evt.oldIndex, evt.newIndex);
        },
      });
      sortables.push(sortable);
    });
    return () => {
      sortables.forEach((s) => s.destroy());
    };
  }, [openedCountries, updateLeagueOrder]);


  useEffect(() => {
    if (!pinnedLeaguesRefs.current) return;
    const sortables = [];
    Object.entries(pinnedLeaguesRefs.current).forEach(([sportId, el]) => {
      if (!el) return;
      const sortable = Sortable.create(el, {
        animation: 150,
        handle: ".drag-handle-pinned-leagues",
        onEnd: (evt) => {
          const oldIndex = evt.oldIndex;
          const newIndex = evt.newIndex;
          setPinnedLeagues((prev) => {
            const sportPinned = prev.filter((l) => l.sport_id === sportId);
            const other = prev.filter((l) => l.sport_id !== sportId);
            const arr = [...sportPinned];
            const [moved] = arr.splice(oldIndex, 1);
            arr.splice(newIndex, 0, moved);
            const reindexed = arr.map((l, idx) => ({ ...l, order: idx }));

            const previous = prev;
            const toastKey = `pinned-league-order-${sportId}`;
            message.open({
              key: toastKey,
              type: "success",
              content: "Order updated successfully!",
            });
            (async () => {
              try {
                await reorderEntities(
                  "LEAGUE",
                  reindexed.map((l) => l.reorder_id || l.id.replace("LEAGUE#", "")),
                  sportId
                );
              } catch (err) {
                console.error("Error updating pinned leagues order", err);
                setPinnedLeagues(previous);
                message.open({
                  key: toastKey,
                  type: "error",
                  content: "Error updating order. Please try again.",
                });
              }
            })();

            return [...other, ...reindexed];
          });
        },
      });
      sortables.push(sortable);
    });
    return () => sortables.forEach((s) => s.destroy());
  }, [openedSports, pinnedLeagues, reorderEntities]);

  useEffect(() => {
    // data load handled by react-query
  }, []);

  return (
    <div className="sorting-table-wrapper">
      <div className="all-sports-header">
        <div className="all-sports-title">
          <h2>All Sports</h2>
        </div>
        <div className="all-sports-actions">
          <button
            className={`save-changes-button ${hasUnsavedChanges ? 'has-changes' : ''}`}
            onClick={handleSaveChanges}
            disabled={!hasUnsavedChanges || isSaving}
          >
            {isSaving ? (
              <>
                <LoadingOutlined className="save-button-loader" />
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
      <table className="sorting-table">
        <thead>
          <tr className="sorting-table-header-row">
            <th className="sorting-table-header-cell"><div className="sorting-table-header-cell-content"><span># Order</span></div></th>
            <th className="sorting-table-header-cell"><div className="sorting-table-header-cell-content"><span>Icon</span></div></th>
            <th className="sorting-table-header-cell"><div className="sorting-table-header-cell-content"><span>Sport</span></div></th>
            <th className="sorting-table-header-cell"><div className="sorting-table-header-cell-content"><span>Status</span></div></th>
            <th className="sorting-table-header-cell"><div className="sorting-table-header-cell-content"><span>Activity</span></div></th>
            <th className="sorting-table-header-cell"><div className="sorting-table-header-cell-content"></div></th>
          </tr>
        </thead>
        <tbody ref={allSportsTableBodyRef} className="fm-individual-sport-table-country-container">
          {dataLoaded && allSports.map((sport, index) => (
            <React.Fragment key={sport.id}>
              <tr className="category-row">
                <td className="category-row__cell drag-handle">
                  <div className="category-row__cell-order">
                    <img src={ClipPath} alt="" />
                    {index + 1}
                  </div>
                </td>
                <td className="category-row__cell">
                  <div className="category-row__live-icon">
                    <TrophyOutlined className="fm-all-sports-table-sport-icon" />
                    <img
                      className="fm-all-sport-table-sport-icon-img"
                      src={getSportLogoUrl(sport.name)}
                      alt={`${sport.name} logo`}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                </td>
                <td className="category-row__cell">{sport.name}</td>
                <td className="category-row__cell">
                  <ToggleSwitch status={!!sport.status} eventCode={sport.id} updateEventCode={updateSportStatus} />
                </td>
                <td className="category-row__cell"></td>
                <td className="category-row__cell">
                  <button onClick={() => setOpenedSports((s) => s.includes(sport.id) ? s.filter((id) => id !== sport.id) : [...s, sport.id])}>
                    {openedSports.includes(sport.id) ? <DownOutlined /> : <UpOutlined />}
                  </button>
                </td>
              </tr>
              {openedSports.includes(sport.id) && (
                <tr>
                  <td colSpan={6}>
                    <div className="fm-all-sport-table-pinned-leagues-container">
                      <div className="fm-all-sport-table-pinned-leagues-header">
                        <h3>{sport.name} Pinned leagues</h3>
                      </div>
                      <div
                        className="fm-all-sport-table-pinned-leagues-list"
                        ref={(el) => {
                          if (el) pinnedLeaguesRefs.current[sport.id] = el;
                        }}
                      >
                        {pinnedLeagues
                          .filter((league) => league.sport_id === sport.id)
                          .map((league) => (
                            <div
                              className="fm-all-sport-table-pinned-leagues-list-item drag-handle-pinned-leagues"
                              key={league.id}
                              data-id={league.id}
                              data-order={league.order}
                            >
                              <img src={ClipPath} alt="ClipPath" />
                              <div className="fm-all-sport-table-pinned-leagues-list-item-text-container">
                                <img
                                  className="fm-all-sport-table-sport-icon"
                                  src={league.logo_url || league.league_logo_url || Planet}
                                  alt={`${league.name} logo`}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = Planet;
                                  }}
                                />
                                <span className="fm-all-sport-table-pinned-leagues-list-item-text">
                                  {league.name}
                                </span>
                              </div>
                              <PushpinFilled
                                style={{ color: "#FD6F27", cursor: "pointer" }}
                                onClick={() => unPinLeague(sport.id, league)}
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                    <div className="fm-all-sport-table-countries-container">
                         {(!sport.countries || sport.countries.length === 0) && (
                        <div style={{ padding: "12px 16px", color: "#888" }}>
                          No data found for this sport.
                        </div>
                      )}
                      <table className="fm-individual-sport-table">
                        {/* Countries container: sortable across countries */}
                        <tbody
                          ref={(el) => {
                            if (el) countryRefs.current[sport.id] = el;
                          }}
                          className="fm-individual-sport-table-country-container"
                        >
                          {sport.countries.map((country) => (
                            <React.Fragment key={country.id}>
                              <tr className="category-row children-row" data-sport-id={sport.id} data-id={country.id} data-order={country.order} data-country-id={country.id}>
                                <td className="category-row__cell"></td>
                                <td className="category-row__cell">
                                  <div className="fm-individual-sport-table-pin-container">
                                    <div className="drag-handle drag-handle-country fm-individual-sport-table-order-container">
                                      <img src={ClipPath} alt="ClipPath" />
                                    </div>
                                    <div className="fm-individual-sport-table-sport-icon-container">
                                      <img
                                        className="fm-individual-sport-table-img"
                                        src={country.flag_url || country.country_flag_url || Planet}
                                        alt={`${country.name} flag`}
                                        onError={(e) => {
                                          e.currentTarget.onerror = null;
                                          e.currentTarget.src = Planet;
                                        }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td className="category-row__cell">{country.name}</td>
                                <td className="category-row__cell">
                                  <ToggleSwitch status={!!country.status} eventCode={`${sport.id}#${country.country_id}`} updateEventCode={updateCountryStatus} />
                                </td>
                                <td className="category-row__cell">
                                  <Select
                                    className="fm-individual-sport-table-country-order-select"
                                    options={Array.from({ length: sport.countries.length }, (_, i) => ({ label: `${i + 1}`, value: i }))}
                                    onChange={(value) => updateCountryOrder(sport.id, country.order, value)}
                                    value={country.order}
                                  />
                                </td>
                                <td className="category-row__cell">
                                  <button onClick={() => setOpenedCountries((s) => s.includes(country.id) ? s.filter((id) => id !== country.id) : [...s, country.id])}>
                                    {openedCountries.includes(country.id) ? <DownOutlined /> : <UpOutlined />}
                                  </button>
                                </td>
                              </tr>
                              {openedCountries.includes(country.id) && (
                                <tr>
                                  <td colSpan={6}>
                                    <table className="fm-individual-sport-table" style={{ width: '100%' }}>
                                      <tbody
                                        ref={(el) => {
                                          if (el) leagueRefs.current[country.id] = el;
                                        }}
                                        className="fm-individual-sport-table-league-container"
                                      >
                                        {country.leagues.map((league) => (
                                          <tr
                                            key={`${country.id}__${league.id}`}
                                            className="fm-individual-sport-table-league-row drag-handle-league children-row"
                                            data-sport-id={sport.id}
                                            data-id={league.id}
                                            data-order={league.order}
                                            data-country-id={country.id}
                                          >
                                            <td className="category-row__cell"></td>
                                            <td className="category-row__cell">
                                              <div className="fm-individual-sport-table-pin-container">
                                                <div className="drag-handle fm-individual-sport-table-order-container">
                                                  <img src={ClipPath} alt="ClipPath" />
                                                </div>
                                                <div className="fm-individual-sport-table-sport-icon-container">
                                                  <img
                                                    className="fm-individual-sport-table-img"
                                                    src={league.logo_url || league.league_logo_url || Planet}
                                                    alt={`${league.name} logo`}
                                                    onError={(e) => {
                                                      e.currentTarget.onerror = null;
                                                      e.currentTarget.src = Planet;
                                                    }}
                                                  />
                                                </div>
                                                {pinnedLeagues.find((l) => l.id === league.id) ? (
                                                  <PushpinFilled className="fm-individual-sport-table-pin-icon pinned" onClick={() => unPinLeague(sport.id, league)} />
                                                ) : (
                                                  <PushpinOutlined className="fm-individual-sport-table-pin-icon" onClick={() => pinLeague(sport.id, league)} />
                                                )}
                                              </div>
                                            </td>
                                            <td className="category-row__cell">{league.name}</td>
                                            <td className="category-row__cell">
                                              <ToggleSwitch status={!!league.status} eventCode={league.id} updateEventCode={updateLeagueStatus} />
                                            </td>
                                            <td className="category-row__cell">
                                              <Select
                                                className="fm-individual-sport-table-league-order-select"
                                                options={Array.from({ length: country.leagues.length }, (_, i) => ({ label: `${i + 1}`, value: i }))}
                                                onChange={(value) => updateLeagueOrder(sport.id, country.id, league.order, value)}
                                                value={league.order}
                                              />
                                            </td>
                                            <td className="category-row__cell"></td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
  
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AllSports;

